import "./auth-club.css";
import {BRAND} from '../shared/brand.js';
import { ArrowLeft, ArrowRight, ShieldCheck, UserCircle } from "@phosphor-icons/react";
import { BrandLockup, SummitMark } from "./Brand";
import { lazy, Suspense, useEffect, useState, type FormEvent } from "react";
import { api } from "./api";
import { rememberCampaign } from "../shared/campaign";
import { BAND_DEFAULTS, type Band } from "../shared/economy";
import { ParentDashboard } from "./ParentDashboard";
import { StudentDashboard } from "./StudentDashboard";
import type { BootstrapData } from "./types";

type Role = "parent" | "student" | null;

interface MeData {
  role: Role;
  // An operator's read-only support view of this family (docs/launch/ADMIN.md).
  readOnly?: boolean;
}

const AuditHub=lazy(()=>import('./math/AuditHub'));
const PuzzleGallery=lazy(()=>import('./math/puzzles/PuzzleGallery'));
const StoryPreview=lazy(()=>import('./math/story/StoryExperience').then(m=>({default:m.StoryPreview})));
const CourseReview=lazy(()=>import('./math/CourseReview'));
const TeacherReview=lazy(()=>import('./math/TeacherReview'));
// Teacher notes pinned in place: draws nothing unless the household is a reviewer account.
const ReviewerNotes=lazy(()=>import('./ReviewerNotes'));
const Notes=({role,readOnly}:{role:'parent'|'student';readOnly?:boolean})=><Suspense fallback={null}><ReviewerNotes role={role} readOnly={readOnly}/></Suspense>;
export function App(){
  if(window.location.pathname.replace(/\/$/,'')==='/math-audit')return <Suspense fallback={<main className="loader-screen">Opening audit hub…</main>}><AuditHub/></Suspense>;
  if(window.location.pathname.replace(/\/$/,'')==='/math-puzzle-preview')return <Suspense fallback={<main className="loader-screen">Opening the puzzle workshop…</main>}><PuzzleGallery/></Suspense>;
  if(window.location.pathname.replace(/\/$/,'')==='/math-story-preview')return <Suspense fallback={<main className="loader-screen">Opening Picnic Bridge…</main>}><StoryPreview/></Suspense>;
  if(window.location.pathname.replace(/\/$/,'')==='/math-course-preview')return <Suspense fallback={<main className="loader-screen">Opening course preview…</main>}><CourseReview/></Suspense>;
  if(window.location.pathname.replace(/\/$/,'')==='/algebra-test-preview')return <Suspense fallback={<main className="loader-screen">Opening Algebra preview…</main>}><TeacherReview/></Suspense>;
  return <FamilyApp/>;
}

function FamilyApp() {
  const [bootstrap, setBootstrap] = useState<BootstrapData | null>(null);
  const [role, setRole] = useState<Role>(null);
  const [supportView, setSupportView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error,setError]=useState("");
  const [resetToken] = useState(() => {
    if (typeof window === "undefined" || window.location.pathname !== "/reset") return "";
    return new URLSearchParams(window.location.search).get("token") ?? "";
  });

  // /join?invite=... opens sign-up for an invited family or parent.
  const [inviteCode] = useState(() => {
    if (typeof window === "undefined" || window.location.pathname.replace(/\/$/, "") !== "/join") return "";
    return new URLSearchParams(window.location.search).get("invite") ?? "";
  });

  async function refreshBootstrap() {
    const data = await api<BootstrapData>("/api/bootstrap");
    setBootstrap(data);
    return data;
  }

  useEffect(() => {
    void (async () => {
      try {
        const [boot, me] = await Promise.all([refreshBootstrap(), api<MeData>("/api/me")]);
        setBootstrap(boot);
        setRole(me.role);
        setSupportView(Boolean(me.readOnly));
      } catch(e){setError(e instanceof Error?e.message:`Could not connect to ${BRAND.platform}`);} finally {
        setLoading(false);
      }
    })();
  }, []);

  async function logout() {
    await api<{ ok: boolean }>("/api/auth/logout", { method: "POST", body: "{}" });
    setRole(null);
    await refreshBootstrap();
  }

  // /reset?token=... is served by the SPA fallback, so it is handled here
  // rather than by a router. Checked before auth: the whole point is that the
  // person following the link cannot sign in.
  if (resetToken) return <ResetScreen token={resetToken} appName={bootstrap?.appName ?? BRAND.platform} />;
  if (inviteCode) return <JoinScreen code={inviteCode} appName={bootstrap?.appName ?? BRAND.platform} />;

  if(error&&!bootstrap)return <main className="loader-screen"><p role="alert">{error}</p><button className="button primary" onClick={()=>window.location.reload()}>Try again</button></main>;
  if (loading || !bootstrap) return <LaunchLoader />;
  if (!bootstrap.setupComplete) {
    return <SetupScreen appName={bootstrap.appName} requiresSetupToken={bootstrap.requiresSetupToken} onComplete={async () => {
      await refreshBootstrap();
      setRole("parent");
    }} />;
  }
  if (role === "student") return <><StudentDashboard onLogout={logout} /><Notes role="student" /></>;
  if (role === "parent" && supportView) return <>
    <div className="support-view-banner" role="status">Read-only support view. Nothing you do here is saved. <button type="button" onClick={async () => { await logout(); window.location.href = "/operator/"; }}>End view</button></div>
    <ParentDashboard onLogout={async () => { await logout(); window.location.href = "/operator/"; }} />
    <Notes role="parent" readOnly />
  </>;
  if (role === "parent") return <><ParentDashboard onLogout={logout} /><Notes role="parent" /></>;
  return <LoginScreen bootstrap={bootstrap} onLogin={setRole} onFamilyChange={refreshBootstrap} />;
}

// Sign-up from an invite link. A family invite also names the family and sets its timezone; a
// parent invite joins the family that sent it.
function JoinScreen({ code, appName }: { code: string; appName: string }) {
  const [invite, setInvite] = useState<{ kind: "family" | "parent"; familyName: string | null; membershipTier?: "founding" | "regular"; reviewer?: boolean; offer?: {first:{monthly:number;annual:number};additional:{monthly:number;annual:number}} } | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const defaultZone = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"; } catch { return "America/New_York"; } })();
  useEffect(() => {
    void api<{ kind: "family" | "parent"; familyName: string | null; membershipTier?: "founding" | "regular"; reviewer?: boolean; offer?: {first:{monthly:number;annual:number};additional:{monthly:number;annual:number}} }>(`/api/invite/${encodeURIComponent(code)}`)
      .then(setInvite).catch((caught) => setError(caught instanceof Error ? caught.message : "This invite link isn’t valid"));
  }, [code]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    if (String(form.get("password")) !== String(form.get("confirm"))) { setError("Those passwords do not match"); return; }
    setBusy(true); setError("");
    try {
      await api("/api/signup", { method: "POST", body: JSON.stringify({
        invite: code, name: form.get("name"), email: form.get("email"), password: form.get("password"),
        ...(invite?.kind === "family" ? { familyName: form.get("familyName"), timezone: form.get("timezone"), campaign: rememberCampaign(window.location.search) } : {}),
      }) });
      window.location.assign("/");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not create the account");
      setBusy(false);
    }
  }
  return (
    <main className="login-screen club-auth">
      <div className="club-orbits" aria-hidden="true" />
      <header className="login-header"><BrandLockup appName={appName} width={300} markSize={44} /></header>
      <section className="login-card">
        <span className="eyebrow">{invite?.kind === "parent" ? "Join your family" : invite?.reviewer ? "Reviewer invitation" : "You’re invited"}</span>
        <h1>{invite?.kind === "parent" ? `Join ${invite.familyName ?? "your family"}` : invite?.reviewer ? "Create your reviewer account" : "Create your family’s account"}</h1>
        {!invite && !error && <p className="muted">Checking your invite…</p>}
        {!invite && error && <><p className="form-error">{error}</p><a className="button primary wide" href="/">Go to sign in</a></>}
        {invite && (
          <form onSubmit={submit}>
            {invite.kind === "family" && <>
              {invite.reviewer && <p className="reviewer-note"><strong>This is a free reviewer account.</strong> You can add practice students and try every grade, including the K–2 read-aloud lessons. Please use made-up names only: no real student’s name or information.</p>}
              {invite.offer && <p><strong>{invite.membershipTier === "founding" ? "Founding family invitation" : "Family invitation"}: ${invite.offer.first.annual}/year for your first child and ${invite.offer.additional.annual}/year for each additional child</strong> (or ${invite.offer.first.monthly} and ${invite.offer.additional.monthly} every 4 weeks). Summer is included with yearly. {invite.membershipTier === "founding" ? "Your rate stays locked while you stay subscribed. " : ""}30-day money-back guarantee on your first payment. Payment comes after account setup.</p>}
              <label>{invite.reviewer ? "Account name" : "Family name"}<input name="familyName" maxLength={60} placeholder={invite.reviewer ? "Classroom preview" : "Your family"} required autoFocus /></label>
              <label>Family timezone<input name="timezone" defaultValue={defaultZone} required /><small>Each day’s goal resets at midnight here.</small></label>
            </>}
            <label>Your name<input name="name" maxLength={80} autoComplete="name" required autoFocus={invite.kind === "parent"} /></label>
            <label>Email<input name="email" type="email" autoComplete="username" required /></label>
            <label>Password <small>at least 8 characters</small><input name="password" type="password" minLength={8} autoComplete="new-password" required /></label>
            <label>Confirm password<input name="confirm" type="password" minLength={8} autoComplete="new-password" required /></label>
            {error && <p className="form-error">{error}</p>}
            <button className="button primary wide" disabled={busy}>{busy ? "Creating…" : invite.kind === "family" ? "Create our account" : "Join the family"}</button>
            {invite.kind === "family" && <p className="muted">{invite.reviewer ? "Next, add practice students and explore the lessons." : "Next you’ll review your family membership. When enrollment opens, you’ll complete payment before adding children and giving each a 4-digit PIN."}</p>}
            <p className="muted">By creating an account you agree to the <a href="/legal/terms/">terms</a> and <a href="/legal/privacy/">privacy policy</a>.</p>
          </form>
        )}
      </section>
      <AuthFooter />
    </main>
  );
}

function ResetScreen({ token, appName }: { token: string; appName: string }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") ?? "");
    if (password !== String(form.get("confirm") ?? "")) { setError("Those passwords do not match"); return; }
    setBusy(true); setError("");
    try {
      await api("/api/auth/parent/reset", { method: "POST", body: JSON.stringify({ token, password }) });
      setDone(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not reset the password");
    } finally { setBusy(false); }
  }

  return (
    <main className="login-screen club-auth">
      <div className="club-orbits" aria-hidden="true" />
      <header className="login-header"><BrandLockup appName={appName} width={300} markSize={44} /></header>
      <section className="login-card">
        <span className="eyebrow">Password reset</span>
        <h1>{done ? "All set" : "Choose a new password"}</h1>
        {done ? (
          <>
            <p className="muted">Your password has been changed. Sign in with your new password.</p>
            <a className="button primary wide" href="/">Sign in</a>
          </>
        ) : (
          <form onSubmit={submit}>
            <label>New password <small>at least 8 characters</small>
              <input name="password" type="password" minLength={8} autoComplete="new-password" required autoFocus /></label>
            <label>Confirm new password
              <input name="confirm" type="password" minLength={8} autoComplete="new-password" required /></label>
            {error && <p className="form-error">{error}</p>}
            <button className="button primary wide" disabled={busy}>{busy ? "Saving…" : "Set new password"}</button>
          </form>
        )}
      </section>
      <AuthFooter />
    </main>
  );
}

function LaunchLoader() {
  return (
    <main className="loader-screen">
      <div className="loader-orbit"><SummitMark size={38} /></div>
      <span>Opening your club…</span>
    </main>
  );
}

function Brand({ name }: { name: string }) {
  return <BrandLockup appName={name} width={300} markSize={44} />;
}

function SetupScreen({ appName, requiresSetupToken, onComplete }: { appName: string; requiresSetupToken: boolean; onComplete: () => Promise<void> }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/setup", {
        method: "POST",
        body: JSON.stringify({
          appName: form.get("appName"),
          currencyName: form.get("currencyName"),
          timezone: form.get("timezone"),
          parentPassword: form.get("password"),
          setupToken: form.get("setupToken") || undefined,
        }),
      });
      await onComplete();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Setup failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-shell club-setup">
      <section className="auth-art">
        <Brand name={appName} />
        <div className="auth-art-copy">
          <span className="eyebrow">A good start for every learner</span>
          <h1>{BRAND.tagline}</h1>
          <p className="brand-subtagline">A clear learning path for your child. A simpler daily routine for your family.</p>
        </div>
        <div className="horizon-lines" aria-hidden="true"><i /><i /><i /></div>
      </section>
      <section className="auth-panel">
        <form className="auth-card setup-card" onSubmit={submit}>
          <span className="step-chip">Family setup</span>
          <h2>Welcome to your club</h2>
          <p className="muted">Choose your family’s timezone and parent password to get started.</p>
          <div className="form-grid two">
            <label>App name<input name="appName" defaultValue={BRAND.platform} maxLength={40} required /></label>
            <label>Currency name<input name="currencyName" defaultValue={BRAND.currency} maxLength={24} required /></label>
          </div>
          <label>Family timezone<input name="timezone" defaultValue="America/New_York" required /></label>
          <label>Shared parent password<input name="password" type="password" minLength={8} autoComplete="new-password" required /></label>
          {requiresSetupToken && <label>One-time setup code<input name="setupToken" autoComplete="off" required /><small>Use the setup code provided for your account.</small></label>}
          {error && <p className="form-error">{error}</p>}
          <button className="button primary wide" disabled={busy}>
            {busy ? "Creating…" : "Create family account"}<ArrowRight size={19} weight="bold" />
          </button>
          <p className="secure-note"><ShieldCheck size={18} weight="fill" /> Your account details are kept secure.</p>
        </form>
        <AuthFooter />
      </section>
    </main>
  );
}

function LoginScreen({ bootstrap, onLogin, onFamilyChange }: { bootstrap: BootstrapData; onLogin: (role: Exclude<Role, null>) => void; onFamilyChange: () => Promise<BootstrapData> }) {
  // A device-setup link (/d/…) lands here with ?child= for that child, or ?device=expired.
  const [arrival] = useState(() => { const params = new URLSearchParams(window.location.search); const arrived = { child: params.get("child"), expired: params.get("device") === "expired" }; if (arrived.child || arrived.expired) window.history.replaceState(null, "", window.location.pathname); return arrived; });
  const arrivedChild = bootstrap.students.find((student) => student.id === arrival.child)?.id;
  const [mode, setMode] = useState<"choose" | "student" | "parent" | "forgot">(arrivedChild ? "student" : "choose");
  // A device that has never been told its family shows a family-code box instead of names.
  const familyKnown = bootstrap.familyKnown ?? bootstrap.students.length > 0;
  const [studentId, setStudentId] = useState(arrivedChild ?? bootstrap.students[0]?.id ?? "");
  const [error, setError] = useState(arrival.expired ? "That setup link has expired or was already used. Ask a parent for a new one, or enter your family code." : "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function studentLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true); setError("");
    const pin = new FormData(event.currentTarget).get("pin");
    try {
      await api("/api/auth/student", { method: "POST", body: JSON.stringify({ studentId, pin }) });
      onLogin("student");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Login failed");
    } finally { setBusy(false); }
  }

  async function enterFamilyCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true); setError("");
    const code = new FormData(event.currentTarget).get("code");
    try {
      await api("/api/family", { method: "POST", body: JSON.stringify({ code }) });
      const next = await onFamilyChange();
      setStudentId(next.students[0]?.id ?? "");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That family code didn’t work");
    } finally { setBusy(false); }
  }

  async function switchFamily() {
    setBusy(true); setError("");
    try {
      await api("/api/family/forget", { method: "POST", body: "{}" });
      await onFamilyChange();
      setStudentId("");
    } finally { setBusy(false); }
  }

  async function parentLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true); setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/auth/parent", {
        method: "POST",
        body: JSON.stringify({ email: form.get("email") || undefined, password: form.get("password") }),
      });
      onLogin("parent");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Login failed");
    } finally { setBusy(false); }
  }

  async function requestReset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true); setError("");
    const email = new FormData(event.currentTarget).get("email");
    try {
      await api("/api/auth/parent/forgot", { method: "POST", body: JSON.stringify({ email }) });
      setSent(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not send the reset email");
    } finally { setBusy(false); }
  }

  return (
    <main className="login-screen club-auth">
      <div className="club-orbits" aria-hidden="true" />
      <header className="login-header"><Brand name={bootstrap.appName} /></header>
      <section className="login-card">
        {mode === "choose" && (
          <>
            <span className="eyebrow">Welcome back</span>
            <h1>Let’s get started</h1>
            <p className="muted">Choose how you’d like to sign in.</p>
            {error && <p className="form-error" role="alert">{error}</p>}
            <div className="role-grid">
              <button className="role-card student-role" onClick={() => setMode("student")} disabled={familyKnown && bootstrap.students.length === 0}>
                <span className="role-icon"><SummitMark size={32} /></span>
                <strong>Student</strong><small>{!familyKnown ? "Enter your family code" : bootstrap.students.length ? "Continue your learning" : "Ask a parent to add a student"}</small>
              </button>
              <button className="role-card" onClick={() => setMode("parent")}>
                <span className="role-icon"><ShieldCheck size={30} weight="fill" /></span>
                <strong>Parent</strong><small>View your family’s progress</small>
              </button>
            </div>
          </>
        )}
        {mode === "student" && !familyKnown && (
          <form onSubmit={enterFamilyCode}>
            <button type="button" className="back-button" onClick={() => { setMode("choose"); setError(""); }}><ArrowLeft /> Back</button>
            <span className="eyebrow">Student access</span><h1>Enter your family code</h1>
            <p className="muted">Ask a parent for the code in Family settings. You only need it once on this device.</p>
            <label>Family code<input name="code" autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={20} required autoFocus /></label>
            {error && <p className="form-error">{error}</p>}
            <button className="button primary wide" disabled={busy}>{busy ? "Checking…" : "Find my family"}</button>
          </form>
        )}
        {mode === "student" && familyKnown && (
          <form onSubmit={studentLogin}>
            <button type="button" className="back-button" onClick={() => { setMode("choose"); setError(""); }}><ArrowLeft /> Back</button>
            <span className="eyebrow">Student access</span><h1>Pick your name</h1>
            <div className="student-picker">
              {bootstrap.students.map((student) => (
                <button type="button" key={student.id} className={studentId === student.id ? "selected" : ""} onClick={() => setStudentId(student.id)}>
                  <UserCircle size={26} weight={studentId === student.id ? "fill" : "regular"} />
                  <span>{student.name}</span><small>{BAND_DEFAULTS[student.band as Band]?.grades ?? ""}</small>
                </button>
              ))}
            </div>
            <label className="pin-label">4-digit PIN<input name="pin" inputMode="numeric" pattern="[0-9]{4}" maxLength={4} autoComplete="one-time-code" placeholder="••••" required autoFocus /></label>
            {error && <p className="form-error">{error}</p>}
            <button className="button primary wide" disabled={busy || !studentId}>Start learning <ArrowRight weight="bold" /></button>
            <button type="button" className="text-link center" disabled={busy} onClick={() => void switchFamily()}>
              Not your family{bootstrap.familyName ? ` (${bootstrap.familyName})` : ""}? Use a different family code
            </button>
          </form>
        )}
        {mode === "parent" && (
          <form onSubmit={parentLogin}>
            <button type="button" className="back-button" onClick={() => { setMode("choose"); setError(""); }}><ArrowLeft /> Back</button>
            <span className="eyebrow">For grown-ups</span><h1>Parent sign-in</h1>
            <p className="muted">Sign in with your own email and password.</p>
            <label>Email<input name="email" type="email" autoComplete="username" placeholder="you@example.com" autoFocus /></label>
            <label>Password<input name="password" type="password" autoComplete="current-password" required /></label>
            {error && <p className="form-error">{error}</p>}
            <button className="button primary wide" disabled={busy}>Sign in <ShieldCheck weight="fill" /></button>
            <button type="button" className="text-link center" onClick={() => { setMode("forgot"); setError(""); setSent(false); }}>
              Forgot your password?
            </button>
          </form>
        )}
        {mode === "forgot" && (
          <form onSubmit={requestReset}>
            <button type="button" className="back-button" onClick={() => { setMode("parent"); setError(""); }}><ArrowLeft /> Back</button>
            <span className="eyebrow">Password reset</span><h1>Send a reset link</h1>
            {sent ? (
              <p className="muted">
                If that address has an account, a reset link is on its way. It works once and
                expires in an hour. Check spam if it does not appear.
              </p>
            ) : (
              <>
                <p className="muted">We will email a single-use link that expires in an hour.</p>
                <label>Email<input name="email" type="email" autoComplete="username" required autoFocus /></label>
                {error && <p className="form-error">{error}</p>}
                <button className="button primary wide" disabled={busy}>{busy ? "Sending…" : "Email me a reset link"}</button>
              </>
            )}
          </form>
        )}
      </section>
      <AuthFooter />
    </main>
  );
}

function AuthFooter() {
  return <footer className="login-footer">
    <span>{BRAND.byline}</span>
    <nav aria-label="Legal"><a href="/legal/privacy/">Privacy</a><a href="/legal/terms/">Terms</a></nav>
  </footer>;
}
